import { Injectable } from '@angular/core';
import { FormControl } from '@angular/forms';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import chunk from 'lodash-es/chunk';
import identity from 'lodash-es/identity';
import pickBy from 'lodash-es/pickBy';
import uniq from 'lodash-es/uniq';
import { BehaviorSubject, from, Observable, of } from 'rxjs';
import {
  catchError,
  map,
  mergeMap,
  retryWhen,
  switchMap,
  tap,
  timeout,
  toArray
} from 'rxjs/operators';
import { CreatePatientInput, ModelPatientFilterInput } from '../../../../API';
import { ClinicService } from '../../../core/api/clinic.service';
import { Patient, PatientList, PatientService } from '../../../core/api/patient.service';
import { StaffService } from '../../../core/api/staff.service';
import { PatientFormGroup } from '../../../shared/patient-form/patient-form.model';
import {
  describeRequestError,
  retryTransientRequestErrors
} from '../import-wizard-request-retry';

interface ImportProgress {
  processed: number;
  total: number;
}

// One imported row that could not be created, kept so the wizard can tell the user exactly
// which patients failed and why — instead of silently dropping them (the old regression).
export interface ImportRowFailure {
  name: string;
  reason: string;
}

// Result of the create phase: the patients that were created AND the rows that failed.
// Nothing is discarded silently.
export interface CreatePatientsResult {
  created: Patient[];
  failures: ImportRowFailure[];
}

// Outcome of a single create attempt inside the bounded-concurrency stream.
interface CreatePatientOutcome {
  createdPatient: Patient | null;
  failure: ImportRowFailure | null;
}

@Injectable({
  providedIn: 'root'
})
export class ImportWizardCheckExistingPatientsService {
  // Bounded concurrency keeps the in-flight request count low enough that the browser
  // does not stall and AppSync does not throttle. 12 keeps dedupe of 19K patients under ~15s
  // without saturating the AppSync rate limit in practice.
  private readonly maxConcurrentRequests = 12;
  // Generous per-request hang-guard so a truly wedged mutation cannot block a mergeMap slot
  // forever. Set well ABOVE realistic create latency: a createPatient goes through AppSync
  // client hydration + an offline mutate and, under concurrency, can legitimately take many
  // seconds. The old 10s value was too aggressive and silently timed out valid rows.
  private readonly perRequestTimeoutMs = 60000;
  // Retry budget for provably-transient rejections (see retryTransientRequestErrors) before
  // giving up and recording the row as a failure — a failure is reported, never dropped.
  private readonly maxRetryAttempts = 3;
  private readonly retryBackoffBaseMs = 500;
  // Hard ceiling on the entire dedupe phase. The paginated clinic fetch walks N pages
  // sequentially, so for clinics with tens of thousands of existing patients this can run
  // for a few minutes. Five minutes covers realistic clinic sizes; past that we treat the
  // fetch as failed and let the timeout ERROR propagate to the caller — we must never
  // silently proceed with a partial/empty patient list, because that would flag every
  // import row as new and mass-create duplicates of patients who already exist.
  private readonly dedupePhaseHardCapMs = 300000;
  private readonly createProgressSubject = new BehaviorSubject<ImportProgress>({
    processed: 0,
    total: 0
  });
  public readonly createProgress$: Observable<ImportProgress> = this.createProgressSubject.asObservable();

  constructor(
    private staffService: StaffService,
    private clinicService: ClinicService,
    private patientService: PatientService
  ) {}

  public reduceToUniqueKeys(array: Object[]): string[] {
    return array.map(val => Object.keys(val)).reduce((prev, curr) => uniq([...prev, ...curr]));
  }

  public getAndPatchDuplicates(patientFormGroups: PatientFormGroup[]): Observable<boolean[]> {
    return this.staffService.getLoggedInStaff().pipe(
      switchMap(doctor => {
        const loggedInClinicId =
          doctor && doctor.clinic && doctor.clinic.id ? doctor.clinic.id : '';
        const loggedInDoctorId = doctor && doctor.id ? doctor.id : '';
        // ONE paginated fetch of every clinic patient. The recursive nextToken handling inside
        // getPatientsForClinic walks all pages sequentially — there is no per-chunk hang risk
        // because there is no parallel chunking. The timeout caps total wall time and ERRORS
        // if the fetch overruns; that error is intentionally NOT caught here so the caller can
        // block the import instead of proceeding as if the clinic had no existing patients.
        return this.clinicService
          .getPatientsForClinic(
            loggedInClinicId,
            false,
            0,
            'network-only',
            undefined,
            Number.POSITIVE_INFINITY,
            undefined
          )
          .pipe(
            timeout(this.dedupePhaseHardCapMs),
            map((clinicPatientList: PatientList) => {
              const clinicPatients = (clinicPatientList && clinicPatientList.items) || [];
              // Index every clinic patient by a normalized name key. Multiple patients can share
              // the same name, so each key maps to a list of candidates that the existing match
              // routine then narrows down by email / dob / phone.
              const patientsByNameKey = new Map<string, Patient[]>();
              clinicPatients.forEach(clinicPatient => {
                if (!clinicPatient) {
                  return;
                }
                const nameKey = this.buildNameKey(
                  clinicPatient.firstName,
                  clinicPatient.lastName
                );
                const existing = patientsByNameKey.get(nameKey);
                if (existing) {
                  existing.push(clinicPatient);
                } else {
                  patientsByNameKey.set(nameKey, [clinicPatient]);
                }
              });

              // Walk the import rows and look up each one locally in O(1).
              const duplicates = patientFormGroups.map(() => false);
              patientFormGroups.forEach(
                (patientFormGroup: PatientFormGroup, importRowIndex: number) => {
                  const nameKey = this.buildNameKey(
                    patientFormGroup.controls.firstName.value,
                    patientFormGroup.controls.lastName.value
                  );
                  const candidates = patientsByNameKey.get(nameKey);
                  if (candidates && candidates.length > 0) {
                    const foundPatient = candidates.find(candidate =>
                      this.doesPatientFormGroupEqualPatientObject(patientFormGroup, candidate)
                    );
                    if (foundPatient) {
                      duplicates[importRowIndex] = true;
                      // Removing falsey values before patching, since we'd want to keep values
                      // from csv if database patient has no value for an attribute
                      patientFormGroup.patchValue(pickBy(foundPatient, identity));
                      if (!!foundPatient['createdAt']) {
                        patientFormGroup.registerControl(
                          'createdAt',
                          new FormControl(new Date(foundPatient['createdAt']).toDateString())
                        );
                      }
                      patientFormGroup.controls.patientDoctorId.setValue(loggedInDoctorId);
                    }
                  }
                }
              );
              return duplicates;
            })
          );
      })
    );
  }

  private buildNameKey(firstName: string, lastName: string): string {
    const normalizedFirstName = firstName ? firstName.trim().toLowerCase() : '';
    const normalizedLastName = lastName ? lastName.trim().toLowerCase() : '';
    return normalizedFirstName + '|' + normalizedLastName;
  }

  public createAndPatchNewPatients(patients: PatientFormGroup[]): Observable<CreatePatientsResult> {
    if (patients.length === 0) {
      return of({ created: [], failures: [] });
    }
    const totalPatientCount = patients.length;
    this.createProgressSubject.next({ processed: 0, total: totalPatientCount });
    let processedPatientCount = 0;
    return from(patients).pipe(
      mergeMap(patient => {
        if (patient.controls['createdAt']) {
          patient.controls['createdAt'].setValue(undefined);
        }
        patient.controls.id.setValue(undefined);
        return this.patientService.createPatient(patient.toApiInput() as CreatePatientInput).pipe(
          // Generous hang-guard only (see perRequestTimeoutMs) — not an aggressive cutoff.
          timeout(this.perRequestTimeoutMs),
          // Retry ONLY provably-transient rejections (throttles) with backoff. Timeouts and
          // unknown errors are never replayed: createPatient is not idempotent, and re-issuing
          // a mutation that may have committed would create the same patient twice.
          retryWhen(retryTransientRequestErrors(this.maxRetryAttempts, this.retryBackoffBaseMs)),
          // Patch each form group as its own mutation completes; the closure captures the specific
          // patient FormGroup that triggered the create call, so ordering does not matter.
          map(createdPatient => {
            // A "successful" response with no patient body still means the row was not created —
            // record it as a failure rather than letting it fall through both buckets.
            if (!createdPatient) {
              return {
                createdPatient: null,
                failure: {
                  name: this.buildPatientDisplayName(patient),
                  reason: 'Server returned no patient'
                }
              } as CreatePatientOutcome;
            }
            patient.patchValue(createdPatient as any);
            return { createdPatient: createdPatient, failure: null } as CreatePatientOutcome;
          }),
          // A row that still fails after retries is RECORDED, never dropped — so the wizard can
          // tell the user exactly who was not created and why.
          catchError(createError =>
            of({
              createdPatient: null,
              failure: {
                name: this.buildPatientDisplayName(patient),
                reason: describeRequestError(createError)
              }
            } as CreatePatientOutcome)
          ),
          tap(() => {
            processedPatientCount++;
            this.createProgressSubject.next({
              processed: processedPatientCount,
              total: totalPatientCount
            });
          })
        );
      }, this.maxConcurrentRequests),
      toArray(),
      map((outcomes: CreatePatientOutcome[]) => this.collectCreateOutcomes(outcomes))
    );
  }

  private collectCreateOutcomes(outcomes: CreatePatientOutcome[]): CreatePatientsResult {
    const created: Patient[] = [];
    const failures: ImportRowFailure[] = [];
    outcomes.forEach(outcome => {
      if (outcome.createdPatient) {
        created.push(outcome.createdPatient);
      } else if (outcome.failure) {
        failures.push(outcome.failure);
      }
    });
    return { created: created, failures: failures };
  }

  private buildPatientDisplayName(patient: PatientFormGroup): string {
    const firstName = patient.controls.firstName.value ? patient.controls.firstName.value : '';
    const lastName = patient.controls.lastName.value ? patient.controls.lastName.value : '';
    const fullName = (firstName + ' ' + lastName).trim();
    return fullName ? fullName : 'Unnamed patient';
  }

  public createFilterFindPatientDuplicates(
    patientFormGroups: PatientFormGroup[]
  ): ModelPatientFilterInput[] {
    // AWS limits the size of filters to 4kb, requiring multiple queries with smaller filters instead of one large filter.
    // See https://docs.aws.amazon.com/amazondynamodb/latest/developerguide/Limits.html#limits-expression-parameters
    const patientFilters = chunk(patientFormGroups, 50).map(formGroups => {
      return {
        or: formGroups.map(patientFormGroup => {
          /*
          For mandatory ones, we can assume that these values will already be filled (and will show errors if there are missing data for these columns)
          For optional ones, we won't require that these are all required
         */
          const filter: ModelPatientFilterInput = {};
          const fn = patientFormGroup.controls.firstName.value;
          const ln = patientFormGroup.controls.lastName.value;

          filter.firstName = { eq: fn };
          filter.lastName = { eq: ln };

          return filter;
        })
      };
    });

    return patientFilters;
  }

  // TODO: We should use patientFormGroup.value instead of patientFormGroup
  private doesPatientFormGroupEqualPatientObject(
    patientFormGroup: PatientFormGroup,
    patientObject: Patient
  ): boolean {
    // TODO Pujan https://gitlab.com/clarity-lv/dry-eye-frontend/-/issues/305#note_333049136
    const nameMatches =
      patientFormGroup.controls.firstName.value === patientObject.firstName &&
      patientFormGroup.controls.lastName.value === patientObject.lastName;

    const isEmptyOrNull = val => {
      return val === '' || val === null;
    };

    // We can assume that at least 1 of patientFormGroup's email, dob, phone will be assigned (therefore not be equal to '')
    // Though, if we had a patient that had null email but filled out dob/phone, and searched for null email, should that be considered a match?
    // Ideally, we try matching non-null strings, and if there are none defined (NOT none that are equal), then we try matching for nulls.

    // TODO: Use clinic country instead of defaulting to CA. See https://gitlab.com/clarity-lv/dry-eye-frontend/-/issues/311
    const formGroupPhone =
      parsePhoneNumberFromString(patientFormGroup.controls.phone.value || '', 'CA') || '';
    const patientObjectPhone = parsePhoneNumberFromString(patientObject.phone || '', 'CA') || '';

    const oneNonEmptyOrNullValueMatches =
      (patientFormGroup.controls.email.value === patientObject.email &&
        !isEmptyOrNull(patientFormGroup.controls.email.value)) ||
      (patientFormGroup.controls.dateOfBirth.value === patientObject.dateOfBirth &&
        !isEmptyOrNull(patientFormGroup.controls.dateOfBirth.value)) ||
      (formGroupPhone &&
        patientObjectPhone &&
        formGroupPhone.nationalNumber &&
        patientObjectPhone.nationalNumber &&
        formGroupPhone.nationalNumber === patientObjectPhone.nationalNumber &&
        !isEmptyOrNull(patientFormGroup.controls.phone.value));

    const allOptionalValuesFalsey =
      !patientFormGroup.controls.email.value &&
      !patientFormGroup.controls.phone.value &&
      !patientFormGroup.controls.dateOfBirth.value &&
      !patientObject.email &&
      !patientObject.phone &&
      !patientObject.dateOfBirth;

    const oneNullValueMatches =
      (patientFormGroup.controls.email.value === null && patientObject.email === null) ||
      (patientFormGroup.controls.phone.value === null && patientObject.phone === null) ||
      (patientFormGroup.controls.dateOfBirth.value === null && patientObject.dateOfBirth === null);

    return (
      nameMatches &&
      (oneNonEmptyOrNullValueMatches || (allOptionalValuesFalsey && oneNullValueMatches))
    );
  }
}
